/**
 * Global single-track guard for BACKGROUND music — NOT short SFX (clicks, combat
 * "ugh"/sword one-shots, etc.), which are intentionally allowed to overlap.
 *
 * The app has many independent BGM sources, each owning its own HTMLAudioElement:
 *   - the route-scoped players (start / kingdom / pvp / mini-game) via `useAudioPlayer`
 *   - the persistent PvE singleton (`usePVEAudio`)
 *   - per-hero PvE battle music (`BattleBoard`)
 *   - the MvM hex-battle track (`HexBattlePage`)
 *   - a few page-local <audio> tags (shop / inventory / mining-factory / character-details)
 *
 * With no arbiter, route transitions and stale singletons could leave two tracks
 * playing at once. Every BGM source must start playback through
 * {@link playExclusiveBackgroundMusic} (instead of calling `audio.play()` directly)
 * so starting one track pauses whatever else was playing, and must call
 * {@link releaseBackgroundMusic} when it pauses/stops its element.
 */

let activeEl: HTMLAudioElement | null = null

/** Pause the current background track unless it is `keep`, then record `keep` as active. */
function pauseOthers(keep: HTMLAudioElement | null): void {
  if (activeEl && activeEl !== keep) {
    try {
      activeEl.pause()
    } catch {
      /* element already detached */
    }
  }
  activeEl = keep
}

/**
 * Play `audio` as the single active background track, pausing any other BGM first.
 * Returns the underlying `play()` promise (may reject while autoplay is still blocked).
 */
export function playExclusiveBackgroundMusic(audio: HTMLAudioElement): Promise<void> {
  pauseOthers(audio)
  return audio.play()
}

/** Clear `audio` from the registry after a source pauses/stops it (prevents a stale reference). */
export function releaseBackgroundMusic(audio: HTMLAudioElement | null): void {
  if (audio && activeEl === audio) {
    activeEl = null
  }
}

/** Hard-stop whatever background track is currently playing (e.g. a global mute). */
export function stopAllBackgroundMusic(): void {
  if (activeEl) {
    try {
      activeEl.pause()
    } catch {
      /* detached */
    }
  }
  activeEl = null
}
